'use client';

import { ReactNode, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface ModalWrapperProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  /**
   * 모달 위치
   * - 'center': 화면 중앙 (기본값)
   * - 'bottom': 화면 하단 (bottom sheet)
   * - 'responsive': 모바일에서 하단, 데스크탑에서 중앙
   */
  position?: 'center' | 'bottom' | 'responsive';
  /**
   * 배경 투명도 (0-1)
   * @default 0.5
   */
  backdropOpacity?: number;
  /**
   * 배경 클릭 시 닫기 비활성화
   * @default false
   */
  disableBackdropClose?: boolean;
  /**
   * aria-labelledby ID for the modal title element
   */
  ariaLabelledBy?: string;

  className?: string;
}

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export const ModalWrapper = ({
  isOpen,
  onClose,
  children,
  position = 'center',
  backdropOpacity = 0.5,
  disableBackdropClose = false,
  ariaLabelledBy,
  className,
}: ModalWrapperProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // 모달 열릴 때 첫 번째 포커스 가능한 요소로 포커스 이동
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const active = document.activeElement;
      if (active instanceof HTMLElement) {
        previousActiveElement.current = active;
      }

      const focusableElements =
        modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }

    return () => {
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [isOpen]);

  // 모달이 열릴 때 메인 화면 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // ESC 키로 모달 닫기
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Tab 키 포커스 트랩
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements =
          modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    },
    [onClose],
  );

  // SSR에서는 document가 없으므로 null 반환
  if (!isOpen || typeof document === 'undefined') return null;

  const handleBackdropClick = () => {
    if (!disableBackdropClose) {
      onClose();
    }
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'center':
        return 'items-center justify-center';
      case 'bottom':
        return 'items-end justify-center';
      case 'responsive':
        return 'items-end justify-center md:items-center';
      default:
        return 'items-center justify-center';
    }
  };

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
      className={`fixed inset-0 z-[9999] flex ${getPositionClasses()} ${className ?? ''}`}
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0"
        style={{ backgroundColor: `rgba(10,22,33,${backdropOpacity})` }}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      {children}
    </div>,
    document.body,
  );
};

export default ModalWrapper;
